import { useSelector } from "react-redux";
import {
  selectTabularFields,
  selectTabularFreezedColumns,
  selectTabularGrouping,
  selectTabularShowGroupedColumns,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectDimensions } from "../../../../store/selectors/metaDataSelectors.ts";

import { Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { CalculationType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  CustomColumnField,
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
  GroupingField,
  ReportField,
} from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, DimensionFieldType, MetaItemType } from "../../../../../../../../api/types/svTypes.ts";
import {
  buildCustomColumnConfigChanges,
  isDateAggregationField,
  isDimensionBased,
  newCustomColumnField,
  newDateAggregationField,
} from "../../../../utilities/fieldsHelper.ts";
import type { CustomColumnData, DateCalculationData } from "../../common/fields/custom-field/customColumn.types.ts";
import CustomColumnConditionValueSetup from "../../common/fields/custom-field/CustomColumnConditionValueSetup.tsx";
import CustomColumnDateCalculationSetup from "../../common/fields/custom-field/CustomColumnDateCalculationSetup.tsx";
import CustomFieldOptions from "../../common/fields/custom-field/CustomFieldOptions.tsx";
import CustomFormulaDialog from "../../common/fields/custom-field/CustomFormulaDialog.tsx";
import DateAggregationFieldOptions from "../../common/fields/custom-field/DateAggregationFieldOptions.tsx";
import NewCustomColumnCalculationSetup from "../../common/fields/custom-field/NewCustomColumnCalculationSetup.tsx";
import {
  dateCalculationToAggregation,
  type DateCalculationType,
} from "../../common/fields/custom-field/utilities/dateCalculationTypes.ts";
import { findExistingCustomFormulaField } from "../../common/fields/custom-field/utilities/findExistingCustomFormulaField.ts";
import DropFieldContainer from "../../common/fields/DropFieldContainer.tsx";
import FieldOptionPopupContainer from "../../common/fields/measures/FieldOptionPopupContainer.tsx";
import MeasureFieldOptions from "../../common/fields/measures/MeasureFieldOptions.tsx";
import MetricFieldOptions from "../../common/fields/metrics/MetricFieldOptions.tsx";
import useCanAddCustomFields from "../../common/hooks/useCanAddCustomFields.ts";
import useFieldsDropContainer from "../../common/table/hooks/useFieldsDropContainer.tsx";
import { getKeyValue } from "../../common/table/tableHelper.ts";
import { createTabularField } from "../../common/utilities/createFields.ts";
import AggregationOptionsComponent from "./AggregationOptionsComponent.tsx";
import DimensionFieldOptions from "./field-options/DimensionFieldOptions.tsx";
import FieldsOtherOptions from "./FieldsOtherOptions.tsx";
import { canDropItem } from "./utils/fieldsDropContainerHelper.ts";

interface Props {
  isApiReport?: boolean;
  readonlyMode?: boolean;
}
const TabularFieldsDropContainer = ({ isApiReport = false, readonlyMode = false }: Props) => {
  const fieldsAreaValues = useSelector(selectTabularFields);
  const freezedColumns = useSelector(selectTabularFreezedColumns);
  const showGroupedColumns = useSelector(selectTabularShowGroupedColumns);
  const grouping = useSelector(selectTabularGrouping);
  const dimensions = useSelector(selectDimensions);
  const canAddCustomField = useCanAddCustomFields();

  const [existingCustomFormulaField, setExistingCustomFormulaField] = useState<CustomColumnField | undefined>(
    undefined
  );

  const {
    createItem,
    handleRemove,
    closePopup,
    handleUpdateConfiguration,
    handleDimensionFieldCustomLabelChange,
    moveItem,
    addItem,
    anchorEl,
    currentField,
    removeItem,
    dimensionFields,
    conditions,
    updateItemConfig,
  } = useFieldsDropContainer(fieldsAreaValues, { readonlyMode, grouping, freezedColumns, showGroupedColumns });

  const fieldsAreaRef = useRef<ReportField[]>(fieldsAreaValues);
  useEffect(() => {
    fieldsAreaRef.current = fieldsAreaValues;
  }, [fieldsAreaValues]);

  const groupingRef = useRef<GroupingField[]>(grouping);
  useEffect(() => {
    groupingRef.current = grouping;
  }, [grouping]);

  const handleCanDropItem = useCallback((item: FieldWrapper<DraggableFieldType> | DraggableMetaType) => {
    return canDropItem(item, fieldsAreaRef.current, groupingRef.current);
  }, []);

  const handleDimensionFieldCustomIdChange = useCallback(
    (customId: string) => {
      if (!isDimensionBased(currentField)) {
        return;
      }
      updateItemConfig(currentField, { customId });
    },
    [currentField, updateItemConfig]
  );

  const [customFieldData, setCustomFieldData] = useState<{
    calculationType?: CalculationType;
    dateCalculationType?: DateCalculationType;
    fieldGuid: string | undefined;
    anchorEl: HTMLElement | null;
  } | null>(null);

  const handleCalculationItemClick = (calculationType: CalculationType) => {
    setCustomFieldData({ calculationType, fieldGuid: currentField?.config.guid, anchorEl: anchorEl });
    setExistingCustomFormulaField(findExistingCustomFormulaField(fieldsAreaValues, currentField?.config.guid));
    closePopup();
  };

  const handleDateCalculationItemClick = (type: DateCalculationType) => {
    setCustomFieldData({ dateCalculationType: type, fieldGuid: currentField?.config.guid, anchorEl });
    closePopup();
  };

  const handleApplyCustomCalculation = (data: CustomColumnData) => {
    if (existingCustomFormulaField) {
      updateItemConfig(existingCustomFormulaField, buildCustomColumnConfigChanges(data));
      setCustomFieldData(null);
      return;
    }

    const field = newCustomColumnField(data);
    addItem(createTabularField(field.meta), fieldsAreaValues.length);
    setCustomFieldData(null);
  };

  const handleApplyDateCalculation = (data: DateCalculationData) => {
    const dateDimension = dimensions.find((d) => d.name === data.dateField);
    if (!dateDimension) {
      setCustomFieldData(null);
      return;
    }
    const field = newDateAggregationField(
      dateDimension,
      dateCalculationToAggregation(data.type),
      data.dependsFromFieldId,
      data.name
    );
    addItem(field, fieldsAreaValues.length);
    setCustomFieldData(null);
  };

  const handleEditCustomFormulaCalculationField = (fieldGuid: string) => {
    setCustomFieldData({ calculationType: CalculationType.CustomFormula, fieldGuid, anchorEl: anchorEl });
    setExistingCustomFormulaField(findExistingCustomFormulaField(fieldsAreaValues, fieldGuid));
    closePopup();
  };

  return (
    <>
      <Stack sx={{ gap: ".5rem" }}>
        <DropFieldContainer
          title="Table Fields"
          areaFieldType={AreaItemType.FIELDS}
          acceptedDropTypes={[
            AreaItemType.DIMENSION,
            AreaItemType.MEASURE,
            AreaItemType.METRIC,
            AreaItemType.CONDITIONS,
            MetaItemType.AGGREGATION,
          ]}
          fields={fieldsAreaValues}
          canDropItem={handleCanDropItem}
          renderListItem={createItem}
          getKeyValue={getKeyValue}
          onAddItem={addItem}
          onRemoveItem={removeItem}
          onMoveItem={moveItem}
          createListItem={createTabularField}
          canAddCustomField={canAddCustomField}
          readonly={readonlyMode}
        />
        {customFieldData && (
          <>
            {customFieldData.dateCalculationType != null && (
              <FieldOptionPopupContainer anchorEl={customFieldData.anchorEl} cancel={() => setCustomFieldData(null)}>
                <CustomColumnDateCalculationSetup
                  fieldGuid={customFieldData.fieldGuid}
                  dateCalculationType={customFieldData.dateCalculationType}
                  onClose={() => setCustomFieldData(null)}
                  onApply={handleApplyDateCalculation}
                  readonly={readonlyMode}
                />
              </FieldOptionPopupContainer>
            )}
            {customFieldData.calculationType === CalculationType.ConditionValue && (
              <FieldOptionPopupContainer anchorEl={customFieldData.anchorEl} cancel={() => setCustomFieldData(null)}>
                <CustomColumnConditionValueSetup
                  fieldGuid={customFieldData.fieldGuid}
                  onClose={() => setCustomFieldData(null)}
                  onApply={handleApplyCustomCalculation}
                  readonly={readonlyMode}
                />
              </FieldOptionPopupContainer>
            )}
            {customFieldData.calculationType === CalculationType.PercentageOfTotal && (
              <FieldOptionPopupContainer anchorEl={customFieldData.anchorEl} cancel={() => setCustomFieldData(null)}>
                <NewCustomColumnCalculationSetup
                  fieldGuid={customFieldData.fieldGuid}
                  calculationType={customFieldData.calculationType}
                  onClose={() => setCustomFieldData(null)}
                  onApply={handleApplyCustomCalculation}
                  readonly={readonlyMode}
                />
              </FieldOptionPopupContainer>
            )}
            {customFieldData.calculationType === CalculationType.CustomFormula && (
              <CustomFormulaDialog
                open
                onClose={() => setCustomFieldData(null)}
                onApply={handleApplyCustomCalculation}
                editedField={existingCustomFormulaField}
                sourceFieldGuid={customFieldData.fieldGuid}
              />
            )}
          </>
        )}
        <FieldOptionPopupContainer anchorEl={anchorEl} cancel={closePopup}>
          {currentField?.type === DimensionFieldType.DIMENSION && isDateAggregationField(currentField) && (
            <DateAggregationFieldOptions
              field={currentField}
              saveChanges={handleUpdateConfiguration}
              onRemove={handleRemove}
              cancel={closePopup}
              readonly={readonlyMode}
            />
          )}
          {currentField?.type === DimensionFieldType.DIMENSION && !isDateAggregationField(currentField) && (
            <DimensionFieldOptions
              field={currentField}
              AggregationOptions={
                <AggregationOptionsComponent
                  field={currentField}
                  saveChanges={handleUpdateConfiguration}
                  readonly={readonlyMode}
                />
              }
              cancel={closePopup}
              onRemove={handleRemove}
              onUpdateConfiguration={handleUpdateConfiguration}
              onUpdateCustomLabel={handleDimensionFieldCustomLabelChange}
              isApiReport={isApiReport}
              onUpdateCustomId={handleDimensionFieldCustomIdChange}
              canAddCustomField={canAddCustomField}
              onCalculationItemClick={handleCalculationItemClick}
              onDateCalculationItemClick={handleDateCalculationItemClick}
              readonly={readonlyMode}
            />
          )}
          {currentField?.type === DimensionFieldType.MEASURE && (
            <MeasureFieldOptions
              field={currentField}
              dimensionFields={dimensionFields}
              conditions={conditions}
              otherOptions={
                <FieldsOtherOptions
                  field={currentField}
                  saveChanges={handleUpdateConfiguration}
                  readonly={readonlyMode}
                />
              }
              cancel={closePopup}
              saveChanges={handleUpdateConfiguration}
              onRemove={handleRemove}
              isApiReport={isApiReport}
              onCalculationItemClick={handleCalculationItemClick}
              onDateCalculationItemClick={handleDateCalculationItemClick}
              canAddCustomField={canAddCustomField}
              readonly={readonlyMode}
            />
          )}
          {currentField?.type === DimensionFieldType.METRIC && (
            <MetricFieldOptions
              field={currentField}
              otherOptions={
                <FieldsOtherOptions
                  field={currentField}
                  saveChanges={handleUpdateConfiguration}
                  readonly={readonlyMode}
                />
              }
              cancel={closePopup}
              saveChanges={handleUpdateConfiguration}
              onRemove={handleRemove}
              isApiReport={isApiReport}
              readonly={readonlyMode}
            />
          )}
          {currentField?.type === DimensionFieldType.CUSTOM && (
            <CustomFieldOptions
              field={currentField}
              otherOptions={
                <FieldsOtherOptions
                  field={currentField}
                  saveChanges={handleUpdateConfiguration}
                  readonly={readonlyMode}
                />
              }
              cancel={closePopup}
              saveChanges={handleUpdateConfiguration}
              onRemove={handleRemove}
              isApiReport={isApiReport}
              onEditCustomFormulaCalculationField={handleEditCustomFormulaCalculationField}
              readonly={readonlyMode}
            />
          )}
        </FieldOptionPopupContainer>
      </Stack>
    </>
  );
};

export default TabularFieldsDropContainer;
